import { POST_LAYOUT_ORDER, POST_LAYOUTS } from '@/lib/layouts/registry';
import { CORTES_PRESET_IDS } from '@/lib/layouts/resolveCortesPreset';
import { CIRCULO_PRESET_IDS } from '@/lib/layouts/resolveCirculoPreset';
import type { FieldDescriptor, PostLayoutConfig } from '@/lib/layouts/types';
import type { AssistantMediaItem } from './mediaTypes';

// The layouts the assistant can pick (the editor picker's: the generic kinds
// plus the Cortes and Círculo presets) and their editable fields, simplified
// for the model. Filler videos come from their own catalog and are not offered.

export interface LayoutCatalogField {
  key: string;
  type: 'text' | 'color' | 'number' | 'option' | 'font' | 'image';
  options?: string[];
  min?: number;
  max?: number;
  /** font fields: the config key that holds the font file URL. */
  urlKey?: string;
}

export interface LayoutCatalogEntry {
  kind: string;
  presets: string[];
  fields: LayoutCatalogField[];
}

export interface LayoutCallField {
  key: string;
  value: string | number;
  urlKey?: string;
  url?: string | null;
}

export interface LayoutCall {
  kind: string;
  preset?: string;
  fields: LayoutCallField[];
}

export class LayoutCallError extends Error {
  constructor(readonly code: string, readonly detail?: string) {
    super(code);
  }
}

const PRESETS: Record<string, readonly string[]> = {
  cortes: CORTES_PRESET_IDS,
  circulo: CIRCULO_PRESET_IDS,
};

const CATALOG_KINDS = [...POST_LAYOUT_ORDER, 'cortes', 'circulo'] as const;
const HEX_COLOR = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/;
const MAX_FIELD_TEXT = 300;

function simplify(field: FieldDescriptor): LayoutCatalogField | null {
  switch (field.type) {
    case 'text': return { key: field.key, type: 'text' };
    case 'color': return { key: field.key, type: 'color' };
    case 'slider': return { key: field.key, type: 'number', min: field.min, max: field.max };
    case 'toggle': return { key: field.key, type: 'option', options: field.options.map((option) => option.value) };
    case 'font': return { key: field.key, type: 'font', urlKey: field.urlKey };
    case 'image': return { key: field.key, type: 'image' };
    default: return null;
  }
}

/** Every field the layout can show: fields that depend on a toggle or a
 *  preset are collected across each of their values. */
function fieldsOf(kind: keyof typeof POST_LAYOUTS): LayoutCatalogField[] {
  const def = POST_LAYOUTS[kind];
  const base = def.build({}, null) as PostLayoutConfig;
  const listFor = (config: PostLayoutConfig) => (typeof def.fields === 'function' ? def.fields(config) : def.fields);
  const configs: PostLayoutConfig[] = [base];
  (PRESETS[kind] ?? []).forEach((preset) => configs.push({ ...base, preset } as PostLayoutConfig));
  listFor(base).forEach((field) => {
    if (field.type !== 'toggle') return;
    field.options.forEach((option) => configs.push({ ...base, [field.key]: option.value } as PostLayoutConfig));
  });
  const byKey = new Map<string, LayoutCatalogField>();
  configs.forEach((config) => listFor(config).forEach((field) => {
    const simple = simplify(field);
    if (simple && !byKey.has(simple.key)) byKey.set(simple.key, simple);
  }));
  return [...byKey.values()];
}

export function buildLayoutCatalog(): LayoutCatalogEntry[] {
  return CATALOG_KINDS.map((kind) => ({
    kind,
    presets: [...(PRESETS[kind] ?? [])],
    fields: fieldsOf(kind),
  }));
}

const normalizeName = (value: string) => value.normalize('NFD').replace(/\p{Diacritic}/gu, '')
  .toLowerCase().replace(/\s+/g, ' ').trim();

function fieldValue(
  field: LayoutCatalogField,
  raw: unknown,
  env: LayoutCallEnv,
): LayoutCallField {
  const value = typeof raw === 'number' ? String(raw) : raw;
  if (typeof value !== 'string') throw new LayoutCallError('invalid_value', field.key);
  switch (field.type) {
    case 'text':
      return { key: field.key, value: value.replace(/[\p{Cc}]+/gu, ' ').trim().slice(0, MAX_FIELD_TEXT) };
    case 'color':
      if (!HEX_COLOR.test(value.trim())) throw new LayoutCallError('invalid_color', field.key);
      return { key: field.key, value: value.trim().toUpperCase() };
    case 'number': {
      const number = Number(value);
      if (!Number.isFinite(number)) throw new LayoutCallError('invalid_number', field.key);
      return { key: field.key, value: Math.min(field.max ?? number, Math.max(field.min ?? number, number)) };
    }
    case 'option':
      if (!field.options?.includes(value)) {
        throw new LayoutCallError('invalid_option', `${field.key}: ${field.options?.join(', ')}`);
      }
      return { key: field.key, value };
    case 'font': {
      const wanted = normalizeName(value);
      const font = env.fonts.find((candidate) => candidate.id === value)
        ?? env.fonts.find((candidate) => normalizeName(candidate.name) === wanted);
      if (!font) throw new LayoutCallError('unknown_font', 'Use a name from options.fonts');
      return { key: field.key, value: font.id, urlKey: field.urlKey, url: font.fileUrl };
    }
    case 'image': {
      const item = env.media.get(value);
      if (!item || item.kind !== 'image') throw new LayoutCallError('unknown_media', 'Use an image item_id from editor_search_media');
      return { key: field.key, value: item.url };
    }
    default:
      throw new LayoutCallError('unknown_field', field.key);
  }
}

export interface LayoutCallEnv {
  catalog: readonly LayoutCatalogEntry[];
  /** A campaign enforces this clip's layout. */
  enforced: boolean;
  fonts: ReadonlyArray<{ id: string; name: string; fileUrl: string | null }>;
  media: ReadonlyMap<string, AssistantMediaItem>;
}

/** editor_set_layout({ kind, preset?, fields?: [{ key, value }] }), validated. */
export function validateLayoutCall(args: Record<string, unknown>, env: LayoutCallEnv): LayoutCall {
  if (env.enforced) throw new LayoutCallError('layout_enforced', 'A campaign sets this clip\'s layout');
  const kind = typeof args.kind === 'string' ? args.kind : '';
  if (kind === 'none') return { kind: 'none', fields: [] };
  const entry = env.catalog.find((candidate) => candidate.kind === kind);
  if (!entry) throw new LayoutCallError('unknown_layout', 'Use a kind from options.layouts');
  let preset: string | undefined;
  if (args.preset !== undefined && args.preset !== null) {
    if (typeof args.preset !== 'string' || !entry.presets.includes(args.preset)) {
      throw new LayoutCallError('unknown_preset', entry.presets.join(', ') || 'This layout has no presets');
    }
    preset = args.preset;
  }
  const rawFields = Array.isArray(args.fields) ? args.fields : [];
  const fields = rawFields.map((raw) => {
    const key = raw && typeof raw === 'object' ? (raw as { key?: unknown }).key : undefined;
    const field = entry.fields.find((candidate) => candidate.key === key);
    if (!field) throw new LayoutCallError('unknown_field', String(key));
    return fieldValue(field, (raw as { value?: unknown }).value, env);
  });
  return { kind, ...(preset ? { preset } : {}), fields };
}
